import React, {useEffect, useRef, useState}  from "react";
import { DateRangePicker, SelectPicker } from 'rsuite';
import { useTranslation } from "react-i18next";
import { isEmpty, filter, find  } from "lodash";
import { GetData as GET } from "../../Api";
import DateUtil from "../../utils/date-util.service";
import { startOfDay, subHours } from "date-fns";
import moment from 'moment';

const FactoryFilter = (props) => {
  const showMachines = JSON.parse(props.showMachines ?? false);
  const dateFormat = DateUtil.DATE_RANGE_PICKER_FORMAT;
  const machineType = props.machineType;
  const { t } = useTranslation();
  const factoryRef = React.createRef();
  const lineRef = React.createRef();
  const machineRef = React.createRef();
  const shiftRef = React.createRef();
  let [factories, setFactories] = useState([])
  let [lines, setLines] = useState([]);
  let [machines, setMachines] = useState([]);
  let [shifts, setShifts] = useState([]);
  let [selectedFactory, setSelectedFactory] = useState(props.factory);
  let [selectedLine, setSelectedLine] = useState(props.line);
  let [selectedMachine, setSelectedMachine] = useState(props.machine);
  let [selectedShift, setSelectedShift] = useState(props.shift);
  let startDate = useRef(null);
  let endDate = useRef(null);
  let isRangeSelected = useRef(false);
  let [duration, setDuration] = useState([]);
  let selectedShiftId = useRef(null);
  const getRange = (hours) => {
    const temp = new Date();
    return [subHours(temp, hours), temp];
  };
  const predefinedRanges = [
    { label: 'Last 1 Hour', value: getRange(1), placement: 'left', },
    { label: 'Last 2 Hours', value: getRange(2), placement: 'left', },
    { label: 'Last 4 Hours', value: getRange(4), placement: 'left', },
    { label: 'Last 8 Hours', value: getRange(8), placement: 'left', },
    { label: 'Last 12 hours', value: getRange(12), placement: 'left'},
    { label: 'Last 24 hours', value: getRange(24), placement: 'left'},
    { label: 'Last 2 days', value: getRange(48), placement: 'left'},
    { label: 'Last 3 days', value: getRange(72), placement: 'left'}
  ];
  const getFactories = () => {
    GET('sf-api/factory')
    .then((response)=>{
      if(!isEmpty(response?.data)){
        response.data = response.data.map((factory) => {
          return {...factory, label: factory.name, value: factory.factoryId};
        });
        setFactories(response.data);
      }
    });
  };
  const getLines = (factory) => {
    const lines = factory.lines.join(',');
    let url = `sf-api/lines?id=${lines}`;
    GET(url)
    .then((response)=>{
      if(!isEmpty(response.data)){
        response.data = response.data.map((line) => {
          return {...line, label: line.name, value: line.lineId};
        });
        setLines(response.data);
      }
    });
  };
  const getMachines = (line) => {
    const machines = '';//TODO Need to Revoke this line once Admin Screens integrated for Machines and uncomment below line
    // const machines = line.machines.join(',');
    let url = `sf-api/machines?id=${machines}`;
    GET(url)
    .then((response)=>{
      if(!isEmpty(response.data)){
        response.data = response.data.map((machine) => {
          return {...machine, label: machine.machinename, value: machine.machinename};
        });
        if(!isEmpty(machineType)){
          const tempMachines = filter(response.data, { "machinetypeid": machineType});
          setMachines(tempMachines);
        }else{
          setMachines(response.data);
        }
      }
    });
  };
  const handleFactoryChange = (factoryId)=>{
    if(factoryId !== selectedFactory?.factoryId){
      let factorySelected = find(factories, {factoryId : factoryId});
      setSelectedFactory(factorySelected);
      setLines([]);
      setSelectedLine(null);
      let tempShifts = factorySelected.shifts;
      tempShifts = tempShifts.map((shift)=>{
        return {...shift, label: shift.name, value: shift.id};
      })
      setShifts(tempShifts);
      if(showMachines){
        setMachines([]);
        setSelectedMachine(null);
      }
      handleFactoryBasedDate([startDate.current, endDate.current], factorySelected);
      if(props.hasOwnProperty('onFactoryChange')){
        props.onFactoryChange(factorySelected);
      }
    }
  };
  const handleLineChange = (lineId)=>{
    if(lineId !== selectedLine?.lineId){
      let lineSelected = find(lines, {lineId : lineId});
      setSelectedLine(lineSelected);
      if(showMachines){
        setMachines([]);
        setSelectedMachine(null);
      }
      if(props.hasOwnProperty('onLineChange')){
        props.onLineChange(lineSelected);
      }
    }
  };
  const handleMachineChange = (machineName) => {
    if(machineName !== selectedMachine?.machinename){
      let machineSelected = find(machines, {'machinename' : machineName});
      setSelectedMachine(machineSelected);
      if(props.hasOwnProperty('onMachineChange')){
        props.onMachineChange(machineSelected);
      }
    }
  };
  const addEpochToDate = (dt, milliseconds)=>{
    let temp=startOfDay(dt);
    temp = new Date(temp.valueOf()+milliseconds);
    return temp;
  };
  const updateDuration = (shift) => {
    if(!isNaN(shift?.start) && !isNaN(shift?.end)){
      const startMilliseconds = parseInt(shift?.start, 10);
      const endMilliseconds = parseInt(shift?.end, 10);
      const temp = new Date();
      const tempDuration = [addEpochToDate(temp, startMilliseconds), addEpochToDate(temp, endMilliseconds)];
      isRangeSelected.current = false;
      setDuration(tempDuration);
      if(selectedFactory){
        selectedShiftId.current = shifts[0].id;
      }
      handleFactoryBasedDate(tempDuration, selectedFactory);
      if(props.hasOwnProperty('onDurationChange')){
        props.onDurationChange([startDate.current, endDate.current], isRangeSelected.current);
      }
    }
  };
  const handleShiftChange = (shiftId) => {
    selectedShiftId.current = shiftId;
    if(shiftId !== selectedShift?.id){
      let shiftSelected = find(selectedFactory.shifts, {id: shiftId});
      updateDuration(shiftSelected);
      setSelectedShift(shiftSelected);
      if(props.hasOwnProperty('onShiftChange')){
        props.onShiftChange(shiftSelected);
      }
    }
  };
  const handleFactoryBasedDate = (newDurationValue, factory) => {
    if(factory){
      const startTemp = DateUtil.getTZMomentFromLocalMoment(moment(newDurationValue[0]), factory.timezone);
      const endTemp = DateUtil.getTZMomentFromLocalMoment(moment(newDurationValue[1]), factory.timezone);
      startDate.current = startTemp.toDate();
      endDate.current = endTemp.toDate();
    }else{
      startDate.current = newDurationValue[0];
      endDate.current = newDurationValue[1];
    }
  };
  const handleDurationChange = (newDurationValue, isRange) => {
    isRangeSelected.current = isRange ?? true;
    setDuration(newDurationValue);
    if(selectedFactory){
      const shiftId = shifts[0].id;
      if(shiftId !== selectedShift?.id){
        let shiftSelected = find(selectedFactory.shifts, {id: shiftId});
        setSelectedShift(shiftSelected);
        selectedShiftId.current = shiftId;
        if(props.hasOwnProperty('onShiftChange')){
          props.onShiftChange(shiftSelected);
        }
      }
    }
  };
  const durationUpdated = (newDurationValue) => {
    handleFactoryBasedDate(newDurationValue, selectedFactory);
    if(props.hasOwnProperty('onDurationChange')){
      props.onDurationChange([startDate.current, endDate.current], isRangeSelected.current);
    }
  };
  const handleOkayClick = ()=>{
    isRangeSelected.current = false;
  };
  useEffect(()=>{
    if(!isEmpty(selectedFactory)){
      getLines(selectedFactory);
    }
  },[selectedFactory]);
  useEffect(()=>{
    if(!isEmpty(selectedLine) && showMachines){
      getMachines(selectedLine);
    }
  }, [selectedLine]);
  useEffect(()=>{
    if(!isEmpty(duration)){
      setTimeout(()=>{
        durationUpdated(duration);
      }, 1000); //XXX We need this timeout since, to identify Range or Date's are selected using this Timeout and Okay handler
    }
  }, [duration]);
  useEffect(()=>{
    getFactories();
  },[]);

  return (
    <div className="d-flex justify-content-between cv-factory-dropdown">
      <section ref={factoryRef}>
        <SelectPicker container={() => factoryRef.current} className="cv-select-picker" placeholder={t("LT_select_factory")} 
          onChange={handleFactoryChange} data={factories} searchable={false} size="sm" cleanable={false}
          style={{ width: 150, border:'none'}}></SelectPicker>
      </section>
      <section ref={lineRef}>
        <SelectPicker container={() => lineRef.current} className="cv-select-picker" placeholder={t("LT_select_line")} 
          onChange={handleLineChange} data={lines} searchable={false} size="sm" cleanable={false}
          style={{ width: 150, border:'none' }}></SelectPicker>
      </section>
      {
        showMachines ? 
        <section ref={machineRef}>
          <SelectPicker container={() => machineRef.current} className="cv-select-picker" placeholder={t("LT_select_machine")} 
            onChange={handleMachineChange} data={machines} searchable={false} size="sm" cleanable={false}
            style={{ width: 150, border:'none' }}></SelectPicker>
        </section> : ""
      }
      <section>
        <DateRangePicker
          onChange={handleDurationChange} className="cv-daterange-picker" format={dateFormat} ranges={predefinedRanges} 
          value={duration} cleanable={false} editable={false} placement="bottomEnd"
          placeholder={t("LT_selection_duration")} showMeridian={true} style={{ width: 300 }}
          onOk={handleOkayClick}/>
      </section>
      <section ref={shiftRef}>
        <SelectPicker container={() => shiftRef.current} className="cv-select-picker" placeholder={t("LT_select_shift")} 
          value={selectedShift?.id} onChange={handleShiftChange} data={shifts} searchable={false} size="sm" cleanable={false}
          style={{ width: 150, border:'none' }}></SelectPicker>
      </section>
      
    </div>
  );
};
export default FactoryFilter;